/**
 * A small menu hanging off a button.
 *
 * Used by the Reprint action on a row and by the bulk bar, so the two behave identically:
 * Escape closes, a click outside closes, the trigger says it is expanded and the panel is a
 * real `role="menu"`. There is no library here and no portal: the panel is a child of the
 * trigger, which is what keeps it with its button inside a scrolling table.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '@/components';

export interface MenuProps {
  label: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  busy?: boolean;
  variant?: 'default' | 'primary' | 'ghost';
  size?: 'sm' | 'md';
  /** Which edge the panel hangs from. A row near the right edge wants `right`. */
  align?: 'left' | 'right';
  children: ReactNode;
}

export function Menu({
  label,
  open,
  onToggle,
  onClose,
  busy = false,
  variant = 'default',
  size = 'sm',
  align = 'left',
  children,
}: MenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    // Capture phase. The Drawer listens on the document too and registered first, so a
    // bubble-phase listener here runs second and Escape closes the whole drawer along with
    // this menu. Capture on the document runs before any bubble listener on it.
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose, open]);

  return (
    <div className="ord-menu" ref={ref} onClick={(event) => event.stopPropagation()}>
      <Button
        size={size}
        variant={variant}
        aria-expanded={open}
        aria-haspopup="menu"
        busy={busy}
        onClick={onToggle}
      >
        {label}
      </Button>
      {open ? (
        <div className="ord-menu__panel" data-align={align} role="menu" aria-label={label}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function MenuItem({
  label,
  hint,
  onSelect,
}: {
  label: string;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="ord-menu__item" role="menuitem" onClick={onSelect}>
      <span>{label}</span>
      {hint ? <span className="ord-menu__hint">{hint}</span> : null}
    </button>
  );
}
